import React, { useEffect, useState } from 'react';
import { Download, Search, TrendingUp, Users, Eye } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '@/components/ui/dropdown-menu';

interface User {
  id?: string;
  _id?: string;
  userId?: string;
  fullName: string;
  email: string;
  createdAt: string;
  status: 'Active' | 'Blocked';
}

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useNavigate } from 'react-router-dom';
import { useBlockUserMutation } from '@/store/api/blockApi';
import { useSearchUsersQuery } from '@/store/api/SearchApi';
import { toast } from 'react-hot-toast';
import Loader from '@/components/Loader';

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { DataTable } from '@/components/common/DataTable';
import { ColumnDef } from '@tanstack/react-table';
import { MoreVertical } from 'lucide-react';
import { useUsersQuery } from '@/store/api/usersAPi';
import DateRangeFilter from '@/pages/user/components/DateFilter';
import { useNewDebounce } from '@/hooks/useNewDebounce';
import { formatShowDate, formatShowTime, formatTimezone } from '@/utils/helperUtils';

export const usersColumns = (
  handleBlockUser: (id: string, status: string) => void,
  handleViewUser: (user: any) => void
): ColumnDef<any>[] => [
  {
    id: 'sno',
    header: 'S. No.',
    cell: ({ row, table }) => {
      const { pageIndex, pageSize } = table.options.meta?.pagination ?? {
        pageIndex: 0,
        pageSize: 0,
      };

      return pageIndex * pageSize + row.index + 1;
    },
  },

  {
    accessorKey: 'fullName',
    header: 'Full Name',
  },
  {
    accessorKey: 'email',
    header: 'Email',
    meta: {
      align: 'left',
    },
  },
  {
    header: 'DOB',
    accessorFn: (row) => formatShowDate(row.meta?.dateOfBirth) || '-',
    meta: {
      align: 'left',
    },
  },
  {
    header: 'Time of Birth',
    accessorFn: (row) => formatShowTime(row.meta?.timeOfBirth) || '-',
  },
  {
    header: 'Latitude',
    accessorFn: (row) => row.meta?.latitude ?? '-',
  },
  {
    header: 'Longitude',
    accessorFn: (row) => row.meta?.longitude ?? '-',
  },
  {
    header: 'Timezone',
    accessorFn: (row) => formatTimezone(row.meta?.timezone),
  },
  {
    accessorKey: 'country',
    header: 'Country',
    cell: ({ row }) => {
      const country = row.original.country || '-';

      return (
        <div title={country}>{country.length > 15 ? country.slice(0, 15) + '...' : country}</div>
      );
    },
  },
  {
    accessorKey: 'state',
    header: 'State',
    cell: ({ row }) => {
      const state = row.original.state || '-';

      return <div title={state}>{state.length > 18 ? state.slice(0, 18) + '...' : state}</div>;
    },
  },
  {
    accessorKey: 'city',
    header: 'City',
    cell: ({ row }) => row.original.city || '-',
  },
  {
    header: 'Moon Sign',
    accessorFn: (row) => row.meta?.sunSign || '-',
  },
  {
    header: 'Ascendant',
    accessorFn: (row) => row.meta?.ascendant || '-',
  },

  {
    accessorKey: 'createdAt',
    header: 'Created',
    cell: ({ row }) => new Date(row.original.createdAt).toLocaleDateString(),
  },
  {
    accessorKey: 'status',
    header: 'Status',
    cell: ({ row }) => (
      <span
        className={`px-3 py-1 rounded-full text-xs font-medium ${
          row.original.status === 'Active'
            ? 'bg-green-200 text-green-600'
            : 'bg-red-500/20 text-red-500'
        }`}
      >
        {row.original.status}
      </span>
    ),
  },

  {
    id: 'view',
    header: 'View',
    cell: ({ row }) => (
      <Button variant="ghost" size="icon" onClick={() => handleViewUser(row.original)}>
        <Eye className="w-4 h-4 text-blue-600" />
      </Button>
    ),
  },

  {
    id: 'action',
    header: 'Action',
    cell: ({ row }) => {
      const user = row.original;

      return (
        <div className="flex justify-center ">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="p-1 rounded hover:bg-muted">
                <MoreVertical className="w-4 h-4" />
              </button>
            </DropdownMenuTrigger>

            <DropdownMenuContent
              align="end"
              className="min-w-0 p-0 bg-transparent border-none shadow-none w-fit "
            >
              <button
                onClick={() => handleBlockUser(user.userId || user.id || user._id, user.status)}
                className={`
    h-8
    px-3
    text-xs
    font-semibold
    rounded-md
    flex
    items-center
    gap-1.5
    transition-all
    duration-300
    border
    shadow-sm
    ${
      user.status === 'Active'
        ? 'border-red-200 text-red-600 bg-red-50 hover:bg-[#001B6B] hover:text-white'
        : 'border-green-200 text-green-600 bg-green-50 hover:bg-green-600 hover:text-white'
    }
  `}
              >
                {user.status === 'Active' ? (
                  <>
                    <span className="text-sm"></span>
                    <span>BLOCK</span>
                  </>
                ) : (
                  <>
                    <span className="text-sm"></span>
                    <span>UNBLOCK</span>
                  </>
                )}
              </button>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      );
    },
  },
];

interface ReportCardProps {
  title: string;
  description: string;
  lastGenerated: string;
  icon: React.ComponentType<{ className?: string }>;
  status: 'ready' | 'generating' | 'failed';
}

function ReportCard({ title, description, lastGenerated, icon: Icon, status }: ReportCardProps) {
  const statusConfig = {
    ready: { color: 'bg-green-100 text-green-800', text: 'Ready' },
    generating: { color: 'bg-yellow-100 text-yellow-800', text: 'Generating...' },
    failed: { color: 'bg-red-100 text-red-800', text: 'Failed' },
  };

  return (
    <Card className="transition-shadow hover:shadow-md">
      <CardHeader className="flex flex-row items-start justify-between pb-2 space-y-0">
        <div className="flex items-start space-x-3">
          <div className="p-2 rounded-lg bg-primary/10">
            <Icon className="w-5 h-5 text-primary" />
          </div>
          <div>
            <CardTitle className="text-base">{title}</CardTitle>
            <p className="mt-1 text-sm text-muted-foreground">{description}</p>
          </div>
        </div>
        <Badge className={statusConfig[status].color}>{statusConfig[status].text}</Badge>
      </CardHeader>
      <CardContent>
        <div className="flex items-center justify-between">
          <span className="text-sm text-muted-foreground">Last generated: {lastGenerated}</span>
          <div className="flex space-x-2">
            <Button variant="outline" size="sm">
              <Download className="w-4 h-4 mr-1" />
              Download
            </Button>
            <Button size="sm">Generate</Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

interface StatCardProps {
  label: string;
  value: string | number;
  icon: React.ComponentType<{ className?: string }>;
}

function StatCard({ label, value, icon: Icon }: StatCardProps) {
  return (
    <div
      className="
        w-[250px] min-w-[250px] max-w-[250px]
        h-[100px]
        
        bg-white
        inner-shadow-blue
        rounded-xl
        px-6 py-5
        transition-all
        duration-500
        hover:bg-[linear-gradient(to_right,#082261,#34065c)]
        hover:text-white"
    >
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium">{label}</p>
          <p className="mt-1 text-2xl font-bold">{value}</p>
        </div>
        <Icon className="w-5 h-5 opacity-80 text-[#09226b] " />
      </div>
    </div>
  );
}

export function ReportsPage() {
  const [dateRange, setDateRange] = useState<string>('lastDay');
  const [userLoginType, setUserLoginType] = useState<string>('all');
  const [reportType, setReportType] = useState<string>('all');
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  const [trigger, setTrigger] = useState(0);
  const {
    data,
    isLoading,
    isFetching: isUsersFetching,
    refetch,
  } = useUsersQuery({
    reportType,
    userLoginType,
    take: pageSize,
    skip: pageIndex * pageSize,
  });

  const activeUsers = data?.totalActive ?? 0;
  const blockedUsers = data?.totalBlocked ?? 0;

  const [dateError, setDateError] = useState<string>('');
  const [showLoader, setShowLoader] = useState(false);
  const [search, setSearch] = useState('');
  const debouncedSearch = useNewDebounce(search, 400);

  const today = new Date();

  const formatDate = (date: Date) => date.toISOString().split('T')[0];

  const applyPreset = (type: string) => {
    let from = new Date();
    let to = new Date();

    switch (type) {
      case 'today':
        break;

      case 'yesterday':
        from.setDate(from.getDate() - 1);
        to.setDate(to.getDate() - 1);
        break;

      case 'thisMonth':
        from = new Date(today.getFullYear(), today.getMonth(), 1);
        break;

      case 'pastMonth':
        from = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        to = new Date(today.getFullYear(), today.getMonth(), 0);
        break;
    }

    setStartDate(from);
    setEndDate(to);
    setDateError('');
  };

  const handleDateChange = ([start, end]: [Date | null, Date | null]) => {
    if (!start && !end) {
      setStartDate(null);
      setEndDate(null);
      setDateError('');
      return;
    }

    setStartDate(start);
    setEndDate(end);

    if (!start || !end) {
      setDateError('Please select both start and end date');
      return;
    }

    if (end < start) {
      setDateError('Start date cannot be greater than end date');
      return;
    }

    if (end > new Date()) {
      setDateError('Future dates are not allowed');
      return;
    }

    setDateError('');
  };

  const searchUsersQuery = useSearchUsersQuery(
    {
      ...(debouncedSearch && { search: debouncedSearch }),
      ...(userLoginType !== 'all' && { userLoginType }),

      ...(startDate &&
        endDate && {
          fromDate: startDate.toISOString(),
          toDate: endDate.toISOString(),
        }),

      take: pageSize,
      skip: pageIndex * pageSize,
    },
    {
      skip: !debouncedSearch && (!startDate || !endDate),
    }
  );

  useEffect(() => {
    setPageIndex(0);
  }, [debouncedSearch, startDate, endDate]);

  const navigate = useNavigate();
  const [blockUser] = useBlockUserMutation();

  const isSearching = !!debouncedSearch;
  const isDateFiltering = !!startDate && !!endDate;

  const isFiltering = isSearching || isDateFiltering;

  const isPaginationFetching = isFiltering ? searchUsersQuery.isFetching : isUsersFetching;

  const tableData = isSearching || isDateFiltering ? searchUsersQuery.data : data;

  const rawUsers = tableData?.data ?? [];
  const statsSource = isFiltering ? searchUsersQuery.data : data;

  const users = React.useMemo(() => {
    if (isSearching && reportType === 'user') {
      return rawUsers.filter((u: any) => u.status === 'Blocked');
    }

    if (isSearching && reportType === 'analytics') {
      return rawUsers.filter((u: any) => u.status === 'Active');
    }

    return rawUsers;
  }, [rawUsers, isSearching, reportType]);

  const totalCount = data?.count ?? 0;
  const totalPages = isFiltering
    ? Math.ceil(users.length / pageSize)
    : Math.ceil(totalCount / pageSize);

  const getDateParams = (range: string) => {
    const now = new Date();
    let fromDate: Date;

    switch (range) {
      case 'lastDay':
        fromDate = new Date(now);
        fromDate.setDate(now.getDate() - 1);
        break;

      case 'lastWeek':
        fromDate = new Date(now);
        fromDate.setDate(now.getDate() - 7);
        break;

      case 'lastMonth':
        fromDate = new Date(now);
        fromDate.setMonth(now.getMonth() - 1);
        break;

      case 'lastYear':
        fromDate = new Date(now);
        fromDate.setFullYear(now.getFullYear() - 1);
        break;

      default:
        return {};
    }

    return {
      fromDate: fromDate.toISOString(),
      toDate: now.toISOString(),
    };
  };
  const dateParams = getDateParams(dateRange);

  const handleBlockUser = async (userId: string, status: string) => {
    try {
      const newStatus = status === 'Active' ? 'Blocked' : 'Active';

      toast.dismiss();

      await blockUser({ userId, status: newStatus }).unwrap();

      toast.success(`User ${newStatus === 'Blocked' ? 'Blocked' : 'Unblocked'} successfully`);

      if (isSearching || isDateFiltering) {
        searchUsersQuery.refetch();
      }

      refetch();
    } catch (error) {
      toast.dismiss();
      toast.error('Failed to update user status');
    }
  };

  const handleViewUser = (user: any) => {
    setShowLoader(true);

    const userId = user.userId || user.id || user._id;

    setTimeout(() => {
      navigate(`/users/details?userId=${userId}`, {
        state: {
          user,
          birthDetails: user.birthDetails,
        },
      });
    }, 200);
  };

  useEffect(() => {
    if (trigger > 0) refetch();
  }, [trigger]);

  useEffect(() => {
    setPageIndex(0);
  }, [reportType, userLoginType]);

  // const paginatedUsers = React.useMemo(() => {
  //   if (!isFiltering) return users;

  //   const start = pageIndex * pageSize;
  //   const end = start + pageSize;
  //   return users.slice(start, end);
  // }, [users, isFiltering, pageIndex, pageSize]);

  const totalUsers = data?.count ?? 0;

  const pageLoading = isLoading || isPaginationFetching || showLoader;
  const calculatedActiveUsers = totalUsers - blockedUsers;

  return (
    <div className="space-y-6">
      {pageLoading && (
        <div className="fixed inset-0 z-50 flex items-center justify-center ">
          <Loader />
        </div>
      )}

      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Users</h1>
        </div>
      </div>

      <div className="flex flex-wrap gap-6 mt-4 ">
        <StatCard label="Total Users" value={isLoading ? '—' : totalUsers} icon={Users} />

        <StatCard
          label="Active Users"
          value={isLoading ? '—' : calculatedActiveUsers}
          icon={TrendingUp}
        />

        <StatCard label="Blocked Users" value={isLoading ? '—' : blockedUsers} icon={Users} />
      </div>

      <Card>
        {/* <CardHeader>
          <CardTitle className="flex items-center">
            <Filter className="w-5 h-5 mr-2" />
            Filters
          </CardTitle>
        </CardHeader> */}
        <CardContent>
          <div className="grid gap-4 md:grid-cols-[1fr_auto] mt-4">
            <div className="space-y-2 relative  max-w-[360px]">
              <Label htmlFor="search">Search User</Label>

              <Search
                size={20}
                className="absolute right-3 top-1/2 -translate-y-[20%] text-muted-foreground"
              />

              <input
                id="search"
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by Name/ Email "
                className="w-full border border-[#001B6B] rounded-lg px-3 py-2 pr-10 outline-none focus:ring-0"
              />
            </div>

            <div className="flex items-start justify-end gap-4">
              <div className="space-y-2 w-[140px]">
                <Label>Users Type</Label>
                <Select value={reportType} onValueChange={setReportType}>
                  <SelectTrigger className="w-full border border-[#001B6B] rounded-lg px-3 py-2 ring-0 focus:ring-0 shadow-none">
                    <SelectValue placeholder="Select users type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Users</SelectItem>
                    <SelectItem value="analytics">Active users</SelectItem>
                    <SelectItem value="user">Block users</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2 w-[160px]">
                <Label>User Login Type</Label>
                <Select value={userLoginType} onValueChange={setUserLoginType}>
                  <SelectTrigger
                    className="
            w-full
            border border-[#001B6B]
            rounded-lg px-3 py-2
            ring-0 focus:ring-0
            shadow-none
          "
                  >
                    <SelectValue placeholder="Select user type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Users</SelectItem>
                    <SelectItem value="Registered">Registered users</SelectItem>
                    <SelectItem value="Google">Google users</SelectItem>
                    <SelectItem value="Guest">Guest users</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <DateRangeFilter
                  startDate={startDate}
                  endDate={endDate}
                  onChange={handleDateChange}
                  error={dateError}
                />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Tabs defaultValue="all" className="space-y-4">
        <TabsContent value="all" className="space-y-4"></TabsContent>
        <TabsContent value="scheduled" className="space-y-4">
          <Card>
            <CardContent className="p-6"></CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <div className="w-full font-lato ">
        <DataTable
          data={users}
          columns={usersColumns(handleBlockUser, handleViewUser)}
          enableSearch={false}
          enableFilters={false}
          enableColumnVisibility={false}
          manualPagination={true}
          pageSize={pageSize}
          pageCount={Math.ceil((tableData?.count ?? 0) / pageSize)}
          onPaginationChange={({ pageIndex, pageSize }) => {
            setPageIndex(pageIndex);
            setPageSize(pageSize);
          }}
          isLoading={pageLoading}
        />
      </div>
    </div>
  );
}
